import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import {
  ProfilingFailureReason,
  ProfilingStatus,
} from '@iwoca/iwocapay-api-client';
import {
  LapiError,
  fetchPostBuyerPayLinkFraudCheck,
  fetchPostBuyerPayLinkThreatmetrixSession,
  fetchPostBuyerThreatmetrixSessionProfilingStatus,
} from '@iwoca/lapi-client/iwocapay';

import logger from '../logger';
import { getThreatMetrixConfig } from './threatMetrixConfig';
import { tmxApi } from '../../api/iwocapayService';
import {
  useGetContext,
  useGetSellerByHandle,
} from '../../api/lending/lapiHooks';
import { useStateKey } from '../../hooks/useStateKey.hook';

const PROFILING_TIMEOUT_MS = 10_000;
export const SESSION_STORAGE_KEY = 'tmx_profiling_session_id';
export const BOUND_STORAGE_KEY = 'tmx_profiling_bound_pay_link_ids';
export const ABANDONED_STORAGE_KEY = 'tmx_profiling_abandoned_pay_link_ids';
export const QUERIED_STORAGE_KEY = 'tmx_session_query_pay_link_ids';
export const QUERY_ABANDONED_STORAGE_KEY =
  'tmx_session_query_abandoned_pay_link_ids';
export const PENDING_STATUS_STORAGE_KEY = 'tmx_profiling_pending_status';
export const SETTLED_STORAGE_KEY = 'tmx_profiling_settled_session_id';

type ThreatMetrixWindow = Window & {
  threatmetrix?: {
    profile: (host: string, orgId: string, sessionId: string) => void;
  };
};

/**
 * Claims the mint, synchronously. The sessionStorage guard cannot do this alone:
 * the session_id is only written once the async mint resolves, so two hook
 * instances mounted in the same tree would both pass that guard in the same
 * commit and mint twice.
 *
 * Released if the mint fails, so a later client-side route can retry it —
 * holding the claim after a failure would leave sessionStorage empty with no
 * instance willing to mint, stranding `profilingSettled` false for the rest of
 * the SPA session.
 */
let mintClaimed = false;

function loadToolkit(
  toolkitUrl: string,
  onLoad: () => void,
  onError: () => void,
) {
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = toolkitUrl;
  script.addEventListener('load', onLoad);
  script.addEventListener('error', onError);
  document.head.appendChild(script);
  return script;
}

const MAX_STATUS_ATTEMPTS = 3;

type PendingStatus = {
  sessionId: string;
  status: ProfilingStatus;
  failureReason?: ProfilingFailureReason;
  occurredAt: string;
  attempts?: number;
};

function queueStatus(pending: PendingStatus) {
  window.sessionStorage.setItem(
    PENDING_STATUS_STORAGE_KEY,
    JSON.stringify(pending),
  );
}

function clearQueuedStatus() {
  window.sessionStorage.removeItem(PENDING_STATUS_STORAGE_KEY);
}

/**
 * Validated so a malformed entry cannot become a poison message — re-posted and
 * logged on every buyer page for the rest of the browser session. Anything that
 * would not produce a valid request (missing session, unknown status, an
 * `occurredAt` that parses to Invalid Date) is discarded rather than retried.
 */
function readPendingStatus(): PendingStatus | null {
  const raw = window.sessionStorage.getItem(PENDING_STATUS_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearQueuedStatus();
    return null;
  }

  const pending = parsed as Partial<PendingStatus>;
  const isValid =
    typeof pending?.sessionId === 'string' &&
    Boolean(pending.sessionId) &&
    Object.values(ProfilingStatus).includes(
      pending.status as ProfilingStatus,
    ) &&
    typeof pending.occurredAt === 'string' &&
    !Number.isNaN(new Date(pending.occurredAt).getTime());

  if (!isValid) {
    clearQueuedStatus();
    return null;
  }

  return pending as PendingStatus;
}

/**
 * A status POST already in flight for this page load. The queued entry is left in
 * sessionStorage until the POST succeeds (so a mid-flight navigation retries it
 * on the next page rather than dropping it), which means the entry alone cannot
 * de-dupe concurrent hook instances — this does.
 */
let statusPostInFlight = false;

function reportStatus(
  { sessionId, status, failureReason, occurredAt }: PendingStatus,
  stateKey: string,
) {
  return fetchPostBuyerThreatmetrixSessionProfilingStatus({
    stateKey,
    sessionId,
    body: {
      status,
      failure_reason: failureReason,
      occurred_at: occurredAt,
    },
  });
}

/**
 * Pay links recorded against a storage key in this browser session — bound, or
 * abandoned as permanently rejected. A set rather than a single id so a second pay
 * link does not discard the record of the first: a buyer who returns to an earlier
 * pay link would otherwise re-post a bind that already landed.
 */
function readPayLinkIds(storageKey: string): string[] {
  const raw = window.sessionStorage.getItem(storageKey);
  if (!raw) {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === 'string')
      : [];
  } catch {
    return [];
  }
}

function hasPayLink(storageKey: string, payLinkId: string) {
  return readPayLinkIds(storageKey).includes(payLinkId);
}

function recordPayLink(storageKey: string, payLinkId: string) {
  const recorded = readPayLinkIds(storageKey);
  if (recorded.includes(payLinkId)) {
    return;
  }

  window.sessionStorage.setItem(
    storageKey,
    JSON.stringify([...recorded, payLinkId]),
  );
}

export function boundSessionKey(sessionId: string, payLinkId: string) {
  return `${sessionId}:${payLinkId}`;
}

/** A bind POST already in flight for this page load. See `recordPayLink`. */
let bindInFlight = false;

/** A session-query POST already in flight for this page load. See `recordPayLink`. */
let sessionQueryInFlight = false;

/**
 * The outcome of the browser session's one profiling attempt, shared across hook
 * instances. Per-component state cannot hold this: the instance that starts the
 * attempt is often not the one still mounted when it resolves (a client-side
 * navigation mid-mint unmounts it, and the next page's instance is blocked by the
 * mint claim), so the surviving instance would report `profilingSettled: false`
 * forever — the state the "proceed" gate is meant to be released by.
 */
type ProfilingOutcome = { complete: boolean; settled: boolean };

const UNSETTLED: ProfilingOutcome = { complete: false, settled: false };
let outcome: ProfilingOutcome = UNSETTLED;
const outcomeListeners = new Set<() => void>();

function setOutcome(next: ProfilingOutcome) {
  outcome = next;
  outcomeListeners.forEach((listener) => listener());
}

function subscribeToOutcome(listener: () => void) {
  outcomeListeners.add(listener);
  return () => {
    outcomeListeners.delete(listener);
  };
}

/** Aborts whatever the in-flight attempt still holds. Only used by tests. */
let abortAttempt: (() => void) | undefined;

/**
 * The module state above outlives a test the way it outlives a page's components,
 * and an attempt deliberately survives unmount — so clearing sessionStorage is not
 * enough on its own. Without this, a mint still in flight at a test boundary
 * resolves into the next test's freshly-cleared storage and suppresses its mint.
 *
 * Called from the global `afterEach` rather than per-spec, so a spec that mounts
 * this hook cannot leak into the next one.
 */
export function resetThreatMetrixProfilingForTests() {
  abortAttempt?.();
  abortAttempt = undefined;
  mintClaimed = false;
  statusPostInFlight = false;
  bindInFlight = false;
  sessionQueryInFlight = false;
  outcome = UNSETTLED;
  outcomeListeners.clear();
}

type BindOutcome = 'bound' | 'retry' | 'abandon';

/**
 * Bind the profiled session to the buyer's PayLinkReceiver, so the Session Query
 * can resolve the device profile at decision time.
 *
 * - `bound` — landed, never attempt this pay link again.
 * - `retry` — worth another go on the next buyer page.
 * - `abandon` — the request itself is wrong, so sending it again cannot help.
 *
 * Fail-open throughout: nothing here blocks the buyer.
 */
async function bindSession(
  sessionId: string,
  payLinkId: string,
  stateKey: string,
): Promise<BindOutcome> {
  try {
    await fetchPostBuyerPayLinkThreatmetrixSession({
      stateKey,
      payLinkId,
      body: { session_id: sessionId },
    });
    return 'bound';
  } catch (error) {
    // 409 is the expected "not yet": the receiver only exists once the buyer has
    // checked out against this pay link, so it stays retryable and silent.
    if (error instanceof LapiError && error.statusCode === 409) {
      return 'retry';
    }

    logger.error('ThreatMetrix session bind failed', { error });

    // Only a malformed request is abandoned. It cannot come good by being sent
    // again, and repeating it would re-POST and log on every remaining page of the
    // journey.
    //
    // 401 and 403 are about the caller's session rather than the request, and
    // both can come good. The state key is read from a query cached with
    // `staleTime: Infinity`, so an expired session yields a stale key and a 401 or
    // 403 that only clears on the full page load which refetches it — and
    // abandonment lives in sessionStorage, which survives exactly that. Treating
    // them as terminal would lose the binding for the rest of the browser session,
    // and with it the device profile at decision time.
    //
    //404 stays retryable too, and is a different fact from the 409: the endpoint
    // resolves the state key itself, so a 404 means the state key is unknown rather
    // than that the receiver is missing. A stale cached key produces exactly that,
    // and it comes good on the page load which refetches it.
    const isMalformed = error instanceof LapiError && error.statusCode === 400;

    return isMalformed ? 'abandon' : 'retry';
  }
}

type SessionQueryOutcome = 'queried' | 'retry' | 'abandon';

async function runSessionQuery(
  payLinkId: string,
  stateKey: string,
): Promise<SessionQueryOutcome> {
  try {
    await fetchPostBuyerPayLinkFraudCheck({ stateKey, payLinkId });
    return 'queried';
  } catch (error) {
    // Both are "not yet", and both are silent. 409 means the receiver does not
    // exist until the buyer checks out against this pay link; 404 means no TMX
    // session is bound to it yet, which the bind above resolves on a later page.
    if (
      error instanceof LapiError &&
      (error.statusCode === 404 || error.statusCode === 409)
    ) {
      return 'retry';
    }

    logger.error('ThreatMetrix session query failed', { error });

    // Only a malformed request is abandoned — repeating it cannot help, and it
    // would re-POST and log on every remaining page of the journey. 401 and 403
    // stay retryable for the same reason as the bind: they are about the caller's
    // session, not the request, and a stale cached state key comes good on the
    // page load that refetches it.
    const isMalformed = error instanceof LapiError && error.statusCode === 400;

    return isMalformed ? 'abandon' : 'retry';
  }
}

function profilingSettledEarlier() {
  const sessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  return (
    Boolean(sessionId) &&
    window.sessionStorage.getItem(SETTLED_STORAGE_KEY) === sessionId
  );
}

function profilingAttemptInFlight() {
  if (outcome.settled || profilingSettledEarlier()) {
    return false;
  }

  return (
    mintClaimed || Boolean(window.sessionStorage.getItem(SESSION_STORAGE_KEY))
  );
}

async function querySessionOnce(payLinkId: string, stateKey: string) {
  if (
    profilingAttemptInFlight() ||
    sessionQueryInFlight ||
    hasPayLink(QUERIED_STORAGE_KEY, payLinkId) ||
    hasPayLink(QUERY_ABANDONED_STORAGE_KEY, payLinkId)
  ) {
    return;
  }

  sessionQueryInFlight = true;

  try {
    const queryOutcome = await runSessionQuery(payLinkId, stateKey);
    if (queryOutcome === 'queried') {
      recordPayLink(QUERIED_STORAGE_KEY, payLinkId);
    }
    if (queryOutcome === 'abandon') {
      recordPayLink(QUERY_ABANDONED_STORAGE_KEY, payLinkId);
    }
  } finally {
    sessionQueryInFlight = false;
  }
}

type ProfilingState = {
  /** True once TMX has fired `tmx_profiling_complete` for this session. */
  profilingComplete: boolean;
  /**
   * True once profiling has resolved either way — completed or timed out.
   * Buyer pages gate "proceed" on this (fail-open: a timeout still settles).
   */
  profilingSettled: boolean;
};

/**
 * Drop-in device-profiling hook for the new ThreatMetrix path. Mints a
 * session_id, loads the toolkit, fires profiling once per browser session and
 * resolves on the TMX `tmx_profiling_complete` message (or a 10s timeout),
 * reporting the outcome to the backend. Returns gate state so a buyer page can
 * block "proceed" until profiling has settled.
 *
 * Gated on the seller being in the `threatmetrix_profiling` pilot, read from the
 * public seller endpoint. That is keyed on the seller handle rather than the
 * buyer, so profiling can fire on a buyer's first render — before they have
 * authenticated or a PayLinkReceiver exists. Ramp the pilot to enable a seller;
 * an empty pilot is the kill switch — it gates the status report and the bind as
 * well as the mint, so emptying it also stops work for sessions already minted
 * into a buyer's sessionStorage.
 *
 * Also disabled in production until the new path is ready to go live, so enabling
 * the pilot only takes effect on non-production environments for now.
 *
 * Minting is anonymous, but reporting the profiling status needs the buyer's
 * session, so the outcome is queued in sessionStorage when profiling settles and
 * posted once a state key exists — on this page or a later one in the journey.
 *
 * Pass `payLinkId` where the page knows it. Once the buyer is authenticated and a
 * PayLinkReceiver exists, the profiled session is bound to it so the Session Query
 * can resolve the device profile at decision time. The binding is attempted on
 * every buyer page until it succeeds, so a page that is too early (no receiver
 * yet) simply defers to the next one — the one exception being a malformed request,
 * which is abandoned for the browser session rather than repeated on every page.
 */
export function useThreatMetrixProfiling(
  sellerHandle?: string,
  payLinkId?: string,
): ProfilingState {
  const { seller, loadingSeller } = useGetSellerByHandle(sellerHandle);
  const { stateKey } = useStateKey();
  const { context } = useGetContext();
  const signupComplete = Boolean(context.user_signup_finished);

  const { complete: profilingComplete, settled } = useSyncExternalStore(
    subscribeToOutcome,
    () => outcome,
  );
  // Profiling already attempted in this browser session: nothing left to wait for,
  // so the gate opens without this instance having run anything (fail-open).
  const [attemptedEarlier] = useState(profilingSettledEarlier);
  const profilingSettled = settled || attemptedEarlier;
  const startedRef = useRef(false);

  const shouldStart =
    Boolean(sellerHandle) &&
    !loadingSeller &&
    Boolean(seller?.pilots?.threatmetrix_profiling);

  useEffect(() => {
    if (!shouldStart || startedRef.current) {
      return;
    }
    startedRef.current = true;

    if (profilingSettledEarlier()) {
      return;
    }

    // Claimed synchronously, before the async mint below, so a second hook
    // instance in the same tree cannot mint a competing session.
    if (mintClaimed) {
      return;
    }
    mintClaimed = true;

    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    window.sessionStorage.removeItem(SETTLED_STORAGE_KEY);

    if (outcome.settled) {
      setOutcome(UNSETTLED);
    }

    // Profiling is scoped to the browser session, not to this component. Once the
    // mint is away it has to run to completion even if the buyer navigates
    // client-side mid-flight: the next page's instance is blocked by the claim
    // (it mounts before this promise resolves), so abandoning here would strand
    // the minted session and leave nothing willing to profile. The outcome goes to
    // the module store, so whichever instance is mounted when it lands sees it.
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let script: HTMLScriptElement | undefined;
    let onMessage: ((event: MessageEvent) => void) | undefined;

    const teardown = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (onMessage) {
        window.removeEventListener('message', onMessage);
        onMessage = undefined;
      }
      if (script) {
        script.remove();
        script = undefined;
      }
    };

    const run = async () => {
      const { orgId, profilingHost, toolkitUrl } = getThreatMetrixConfig();

      const { session_id: sessionId } =
        await tmxApi.createSessionApiTmxSessionPost();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);

      const settle = (
        status: ProfilingStatus,
        failureReason?: ProfilingFailureReason,
      ) => {
        if (settled) {
          return;
        }
        settled = true;
        teardown();

        queueStatus({
          sessionId,
          status,
          failureReason,
          occurredAt: new Date().toISOString(),
        });

        window.sessionStorage.setItem(SETTLED_STORAGE_KEY, sessionId);

        setOutcome({
          complete: status === ProfilingStatus.complete,
          settled: true,
        });
      };

      onMessage = (event: MessageEvent) => {
        if (
          typeof event.data === 'string' &&
          event.data === `tmx_profiling_complete:${sessionId}`
        ) {
          settle(ProfilingStatus.complete);
        }
      };
      window.addEventListener('message', onMessage);

      timeoutId = setTimeout(
        () => settle(ProfilingStatus.failed, ProfilingFailureReason.timeout),
        PROFILING_TIMEOUT_MS,
      );

      script = loadToolkit(
        toolkitUrl,
        () => {
          const { threatmetrix } = window as ThreatMetrixWindow;
          if (threatmetrix) {
            threatmetrix.profile(profilingHost, orgId, sessionId);
          } else {
            settle(ProfilingStatus.failed, ProfilingFailureReason.js_error);
          }
        },
        () => settle(ProfilingStatus.failed, ProfilingFailureReason.blocked),
      );
    };

    // Exposed so the test teardown can drop a listener and timer that would
    // otherwise outlive the test boundary. Production never aborts.
    abortAttempt = teardown;

    void run().catch((error) => {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      mintClaimed = false;
      teardown();
      logger.error('ThreatMetrix profiling failed', { error });
      setOutcome({ complete: false, settled: true });
    });
  }, [shouldStart]);

  // Post the queued outcome once the buyer is authenticated and the seller is on
  // the pilot. The status endpoint needs their session, which usually does not
  // exist yet when profiling settles (often pre-signup), so the report is
  // deferred rather than dropped. Runs on every buyer page until it lands, so a
  // page that is too early defers to the next one. Fail-open: nothing here blocks
  // the buyer.
  useEffect(() => {
    if (!shouldStart || !stateKey || statusPostInFlight) {
      return;
    }

    const pending = readPendingStatus();
    if (!pending) {
      return;
    }

    // Left in sessionStorage until the POST succeeds: a navigation mid-flight
    // then retries on the next page instead of dropping the outcome.
    statusPostInFlight = true;

    void reportStatus(pending, stateKey)
      .then(() => {
        clearQueuedStatus();
      })
      .catch((error) => {
        const attempts = (pending.attempts ?? 0) + 1;
        const isTerminal =
          error instanceof LapiError && error.statusCode === 400;

        if (isTerminal || attempts >= MAX_STATUS_ATTEMPTS) {
          clearQueuedStatus();
        } else {
          queueStatus({ ...pending, attempts });
        }

        logger.error('ThreatMetrix profiling-status report failed', { error });
      })
      .finally(() => {
        statusPostInFlight = false;
      });
  }, [shouldStart, stateKey, profilingSettled]);

  // Bind the profiled session to the buyer's PayLinkReceiver. Separate from
  // profiling because the two happen at different points in the journey: the
  // session is minted on the buyer's first render, but the receiver only exists
  // once they are authenticated and have checked out against a pay link. Runs on
  // every buyer page until it succeeds, so an attempt that is too early defers to
  // the next page rather than losing the binding.
  useEffect(() => {
    if (!shouldStart || !stateKey || !payLinkId || bindInFlight) {
      return;
    }

    const sessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      return;
    }

    const boundKey = boundSessionKey(sessionId, payLinkId);
    if (
      hasPayLink(BOUND_STORAGE_KEY, boundKey) ||
      hasPayLink(ABANDONED_STORAGE_KEY, boundKey)
    ) {
      return;
    }

    // Recorded only once the bind lands, so a navigation mid-flight retries on
    // the next page instead of leaving the key set with no bind on the backend.
    // The in-flight flag de-dupes concurrent instances in the meantime.
    bindInFlight = true;

    void bindSession(sessionId, payLinkId, stateKey)
      .then((bindOutcome) => {
        if (bindOutcome === 'bound') {
          recordPayLink(BOUND_STORAGE_KEY, boundKey);
          if (signupComplete) {
            void querySessionOnce(payLinkId, stateKey);
          }
        } else if (bindOutcome === 'abandon') {
          recordPayLink(ABANDONED_STORAGE_KEY, boundKey);
        }
      })
      .finally(() => {
        bindInFlight = false;
      });
  }, [shouldStart, stateKey, payLinkId, profilingSettled, signupComplete]);

  useEffect(() => {
    if (!shouldStart || !stateKey || !payLinkId || !signupComplete) {
      return;
    }

    const sessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (
      !sessionId ||
      !hasPayLink(BOUND_STORAGE_KEY, boundSessionKey(sessionId, payLinkId))
    ) {
      return;
    }

    void querySessionOnce(payLinkId, stateKey);
  }, [
    shouldStart,
    stateKey,
    payLinkId,
    profilingSettled,
    settled,
    signupComplete,
  ]);

  return { profilingComplete, profilingSettled };
}
